import type { QuickSearchHit, SearchHitKind } from '@/shared/api';

export interface HitNavigation {
  to: string;
  params?: Record<string, string>;
}

/**
 * Zielroute je Treffer-Kind. Objekte/Apartments/Buchungen haben eigene Detailrouten;
 * Finance öffnet die Objekt-Finanzansicht (Rechnung trägt PropertyId), Parking die globale
 * Parkplatzseite. Gast-Treffer erreichen nur Admin/Staff und landen im Buchungskontext.
 */
export function hitToNavigation(hit: QuickSearchHit): HitNavigation {
  switch (hit.kind) {
    case 'apartment':
      return { to: '/properties/$id', params: { id: hit.id } };
    case 'property-group':
      return { to: '/property-groups/$id', params: { id: hit.id } };
    case 'booking':
      return { to: '/bookings/$id', params: { id: hit.id } };
    case 'finance':
      return hit.propertyId
        ? { to: '/properties/$id/finance', params: { id: hit.propertyId } }
        : { to: '/bookings' };
    case 'parking':
      return { to: '/parking' };
    case 'guest':
      return { to: '/bookings' };
  }
}

export const HIT_KIND_LABELS: Record<SearchHitKind, string> = {
  'property-group': 'Objekte',
  apartment: 'Apartments',
  booking: 'Buchungen',
  guest: 'Gäste',
  parking: 'Parkplätze',
  finance: 'Rechnungen',
};

const KIND_ORDER: SearchHitKind[] = [
  'property-group',
  'apartment',
  'booking',
  'guest',
  'parking',
  'finance',
];

export interface HitGroup {
  kind: SearchHitKind;
  label: string;
  items: QuickSearchHit[];
}

/**
 * Gruppiert Treffer nach Kind in stabiler Reihenfolge. Gast-Treffer werden für Rollen ohne
 * PII-Recht (Owner/„Kunde") clientseitig verworfen — Defense-in-Depth; der Server liefert
 * sie ihnen ohnehin nicht (Provider-Audience = Admin/Staff).
 */
export function groupHits(hits: QuickSearchHit[], canSeeGuests: boolean): HitGroup[] {
  const visible = canSeeGuests ? hits : hits.filter((h) => h.kind !== 'guest');
  return KIND_ORDER.map((kind) => ({
    kind,
    label: HIT_KIND_LABELS[kind],
    items: visible.filter((h) => h.kind === kind),
  })).filter((group) => group.items.length > 0);
}
